import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { z } from 'zod';
import { PlatformModal } from './PlatformModal.js';
import { listInvoices } from '../../api/platform.js';

/**
 * Initialize-subscription operator modal (PR-2a follow-up). For a pre-commercial
 * subscription stuck with NULL period dates: the operator sets the first billing
 * window (period start/end), optionally links an existing invoice, and gives a
 * reason. Mirrors ActivateModal's react-hook-form + PlatformModal shape. Server
 * enforces 422 (already initialized) / 400 (bad period) / 404 (invoice),
 * surfaced via errorMessage.
 */
const schema = z
  .object({
    periodStart: z.string().min(1, 'Period start is required'),
    periodEnd: z.string().min(1, 'Period end is required'),
    invoiceId: z.string().optional(),
    reason: z.string().trim().min(1, 'Reason is required').max(500, 'Reason must be 500 characters or fewer'),
  })
  .refine((v) => new Date(v.periodEnd) > new Date(v.periodStart), {
    message: 'Period end must be after period start',
    path: ['periodEnd'],
  });
type Values = z.infer<typeof schema>;

/** YYYY-MM-DD `days` from today (UTC) for the date inputs' defaults. */
function isoDate(daysFromToday: number): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() + daysFromToday);
  return d.toISOString().slice(0, 10);
}

type PeriodLength = '1m' | '3m' | '6m' | '1y' | 'custom';
/** Calendar-month offsets for the presets (no date lib in the web app). */
const PRESET_MONTHS: Record<Exclude<PeriodLength, 'custom'>, number> = { '1m': 1, '3m': 3, '6m': 6, '1y': 12 };

/** Add `months` calendar months to a YYYY-MM-DD string (UTC), back to YYYY-MM-DD. */
function addMonthsIso(ymd: string, months: number): string {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

export function InitializeSubscriptionModal({
  open,
  onClose,
  mode = 'zombie',
  tenantId,
  tenantName,
  isSubmitting = false,
  errorMessage,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  /**
   * `trial` = a trial sub converting to its first paid period (relabelled copy);
   * `zombie` = a legacy `active` sub with no period dates being remediated
   * (original copy). Same endpoint + mechanics; only the wording differs.
   * Defaults to `zombie` to preserve the original behaviour.
   */
  mode?: 'trial' | 'zombie';
  tenantId: string;
  tenantName: string;
  isSubmitting?: boolean;
  errorMessage?: string;
  onConfirm: (values: { periodStart: string; periodEnd: string; invoiceId?: string; reason: string }) => Promise<void> | void;
}): JSX.Element | null {
  const isTrial = mode === 'trial';
  const title = isTrial ? 'Start first paid period' : 'Initialize subscription';
  const submitLabel = isSubmitting ? (isTrial ? 'Starting…' : 'Initializing…') : isTrial ? 'Start paid period' : 'Initialize';
  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors },
    reset,
  } = useForm<Values>({
    resolver: zodResolver(schema),
    // Default = 1-month preset: periodEnd is one calendar month after start.
    defaultValues: { periodStart: isoDate(0), periodEnd: addMonthsIso(isoDate(0), 1), invoiceId: '', reason: '' },
  });

  // Period-length selector. Presets drive periodEnd off periodStart; 'custom'
  // hands periodEnd back to the operator (the bug fix: periodEnd used to never
  // follow periodStart — verified during MokoAfrika activation 2026-06-10).
  const [periodLength, setPeriodLength] = useState<PeriodLength>('1m');
  const periodStart = useWatch({ control, name: 'periodStart' });
  useEffect(() => {
    if (periodLength === 'custom' || !periodStart) return;
    setValue('periodEnd', addMonthsIso(periodStart, PRESET_MONTHS[periodLength]), { shouldValidate: true });
  }, [periodStart, periodLength, setValue]);

  // Existing invoices for this tenant, for the optional link dropdown.
  const { data: invoiceData } = useQuery({
    queryKey: ['platform', 'invoices', tenantId],
    queryFn: () => listInvoices({ organizationId: tenantId, limit: 200 }),
    enabled: open,
  });

  if (!open) return null;

  const handleClose = (): void => {
    reset();
    setPeriodLength('1m');
    onClose();
  };

  return (
    <PlatformModal
      open={open}
      onClose={handleClose}
      title={title}
      footer={
        <>
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="initialize-form"
            disabled={isSubmitting}
            className="px-4 py-2 rounded-md font-medium bg-indigo-primary text-white hover:opacity-90 disabled:opacity-50"
          >
            {submitLabel}
          </button>
        </>
      }
    >
      <form
        id="initialize-form"
        onSubmit={handleSubmit((v) =>
          onConfirm({
            periodStart: new Date(v.periodStart).toISOString(),
            periodEnd: new Date(v.periodEnd).toISOString(),
            invoiceId: v.invoiceId || undefined,
            reason: v.reason,
          }),
        )}
      >
        {isTrial ? (
          <p className="text-sm text-gray-700 mb-3">
            This ends {tenantName}&apos;s trial and starts their first paid billing period. The trial dates are preserved
            as history. Status moves to <span className="font-medium">active</span> and a paid subscription-period is
            opened.
          </p>
        ) : (
          <p className="text-sm text-gray-700 mb-3">
            Set the first billing window for {tenantName}. Status moves to <span className="font-medium">active</span>, the
            period dates are stored, and a subscription-period record is opened.
          </p>
        )}

        <label htmlFor="init-length" className="block text-sm font-medium text-gray-700 mb-1">
          Period length
        </label>
        <select
          id="init-length"
          value={periodLength}
          onChange={(e) => setPeriodLength(e.target.value as PeriodLength)}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        >
          <option value="1m">1 month</option>
          <option value="3m">3 months</option>
          <option value="6m">6 months</option>
          <option value="1y">1 year</option>
          <option value="custom">Custom</option>
        </select>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="init-start" className="block text-sm font-medium text-gray-700 mb-1">
              Period start
            </label>
            <input
              id="init-start"
              type="date"
              {...register('periodStart')}
              disabled={isSubmitting}
              className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
            />
            {errors.periodStart ? <p className="mt-1 text-sm text-red-600">{errors.periodStart.message}</p> : null}
          </div>
          <div>
            <label htmlFor="init-end" className="block text-sm font-medium text-gray-700 mb-1">
              Period end
            </label>
            <input
              id="init-end"
              type="date"
              {...register('periodEnd')}
              disabled={isSubmitting}
              // readOnly (not disabled) under a preset so RHF still submits the
              // computed value — disabled fields are dropped from RHF form data.
              readOnly={periodLength !== 'custom'}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary ${
                periodLength !== 'custom' ? 'bg-gray-100 text-gray-500' : ''
              }`}
            />
            {periodLength !== 'custom' ? (
              <p className="mt-1 text-xs text-gray-500">Computed from period length. Choose “Custom” to edit.</p>
            ) : null}
            {errors.periodEnd ? <p className="mt-1 text-sm text-red-600">{errors.periodEnd.message}</p> : null}
          </div>
        </div>

        <label htmlFor="init-invoice" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Link invoice (optional)
        </label>
        <select
          id="init-invoice"
          {...register('invoiceId')}
          disabled={isSubmitting}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
        >
          <option value="">— none —</option>
          {(invoiceData?.invoices ?? []).map((inv) => (
            <option key={inv.id} value={inv.id}>
              {inv.number} ({inv.status})
            </option>
          ))}
        </select>

        <label htmlFor="init-reason" className="block text-sm font-medium text-gray-700 mb-1 mt-3">
          Reason
        </label>
        <textarea
          id="init-reason"
          {...register('reason')}
          disabled={isSubmitting}
          rows={3}
          className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-primary"
          placeholder="Why this subscription is being initialized…"
        />
        {errors.reason ? <p className="mt-1 text-sm text-red-600">{errors.reason.message}</p> : null}
        {errorMessage ? (
          <p className="mt-2 text-sm text-red-600" role="alert">
            {errorMessage}
          </p>
        ) : null}
      </form>
    </PlatformModal>
  );
}
